/**
 * HUD de combat dessiné en canvas 2D au-dessus du rendu (grammaire gamedesign §12 / LOT-048).
 * Le HUD lit l'état ; il n'est l'autorité d'aucune règle qu'il affiche.
 */
import { Matrix, Vector3, type TargetCamera } from '@babylonjs/core';
import type { GameData, HudData, SectorId } from '../data';
import { Vec3 } from '../sim/core/math';
import type { DamageSource, Ship } from '../sim/entities';
import type { SimEvent } from '../sim/events';
import type { Simulation } from '../sim/simulation';
import { lockProgress } from '../sim/weapons/lock';
import { laserAimPoint } from '../sim/weapons/player';
import type { Settings } from './settings';

type RGB = readonly [number, number, number];
const css = (c: RGB, a = 1) => `rgba(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)},${a})`;

const SOURCE_LABEL: Record<DamageSource, string> = {
  player: 'JOUEUR',
  interceptor: 'INTERCEPTEUR',
  marksman: 'TIREUR',
  heavy: 'LOURD',
  drone: 'DRONE',
  turret: 'TOURELLE',
  boss: 'BOSS',
  unknown: 'INCONNU',
};
const PHASE_LABEL = { opening: 'OUVERTURE', assault: 'ASSAUT', overdrive: 'SURCHARGE' } as const;

/** `shield` : coup entièrement absorbé par le bouclier (indicateur bleu). */
interface DamageMark { time: number; dir: Vec3; source: DamageSource; amount: number; shield: boolean }
interface Contact { ship: Ship; screen: { x: number; y: number; onScreen: boolean; behind: boolean }; distance: number; priority: number }

export class Hud {
  private readonly ctx: CanvasRenderingContext2D;
  private hitTime = -10;
  private killTime = -10;
  private repairTime = -10;
  private repairAmount = 0;
  private repairLabel = 'RÉPARATION';
  /** Dernier coup reçu sur le bouclier (scintillement de la jauge). */
  private shieldHitTime = -10;
  private damages: DamageMark[] = [];
  private noticeText = '';
  private noticeTime = -10;
  private now = 0;
  private sectorAnnounce: { label: string; time: number } | null = null;
  private scorePulse = -10;
  private lastGain = 0;
  tutorialText = '';
  debugText = '';

  constructor(private readonly canvas: HTMLCanvasElement, private data: GameData, private settings: Settings) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponible');
    this.ctx = ctx;
  }

  setSettings(settings: Settings): void { this.settings = settings; }
  setData(data: GameData): void { this.data = data; }

  reset(): void {
    this.hitTime = this.killTime = this.repairTime = this.noticeTime = -10;
    this.damages = [];
    this.sectorAnnounce = null;
  }

  private get hud(): HudData { return this.data.hud; }

  handleEvents(sim: Simulation, events: SimEvent[]): void {
    const player = sim.player;
    for (const e of events) {
      switch (e.type) {
        case 'damage':
          if (e.source === 'player' && e.applied > 0) {
            if (e.killed) this.killTime = this.now;
            else this.hitTime = this.now;
          } else if (e.targetKind === 'player' && e.applied + e.absorbed > 0) {
            const shieldOnly = e.applied <= 0;
            this.damages.push({ time: this.now, dir: Vec3.from(e.fromDirection), source: e.source, amount: e.applied + e.absorbed, shield: shieldOnly });
            if (this.damages.length > 6) this.damages.shift();
            if (e.absorbed > 0) this.shieldHitTime = this.now;
          }
          void player;
          break;
        case 'repair':
          this.repairTime = this.now;
          this.repairAmount = e.amount;
          this.repairLabel = 'RÉPARATION';
          break;
        case 'emergencyRepair':
          this.repairTime = this.now;
          this.repairAmount = e.amount;
          this.repairLabel = 'RÉPARATION D’URGENCE';
          break;
        case 'missionStarted':
          this.notice(`MISSION : ${sim.mission.label.toUpperCase()}`);
          break;
        case 'shieldBroken':
          if (e.shipId === player.id) this.notice('BOUCLIER ROMPU');
          break;
        case 'missileBlocked':
          if (e.reason === 'noLock') this.notice('PAS DE VERROU', true);
          else if (e.reason === 'empty') this.notice('STOCK VIDE — RAVITAILLEZ AU PORTEUR', true);
          else if (e.reason === 'slots') this.notice('NUÉE SATURÉE', true);
          break;
        case 'overheat':
          this.notice('SURCHAUFFE');
          break;
        case 'boostDepleted':
          this.notice('BOOST ÉPUISÉ', true);
          break;
        case 'score':
          this.scorePulse = this.now;
          this.lastGain = e.gained;
          break;
        case 'dock':
          if (e.phase === 'docked') this.notice('À QUAI — RÉPARATION ET RAVITAILLEMENT');
          if (e.phase === 'launched') this.notice('CATAPULTE — BONNE CHASSE');
          break;
        case 'pickupCollected':
          if (e.gained > 0) this.notice(`+${e.gained} MISSILES`);
          break;
        case 'jumpDrive': {
          const target = this.data.zones.zones[e.target ?? '']?.label.toUpperCase() ?? '';
          if (e.phase === 'charging') this.notice(`MODULE DE SAUT — CHARGE VERS ${target}`);
          else if (e.phase === 'cancelled') this.notice('SAUT ANNULÉ');
          else if (e.phase === 'ready') this.notice(`SAUT VERS ${target}`);
          else if (e.reason === 'noDrive') this.notice('AUCUN MODULE DE SAUT (AEGIS, STATION)', true);
          else if (e.reason === 'mission') this.notice('SAUT IMPOSSIBLE PENDANT UNE MISSION', true);
          else if (e.reason === 'noTarget') this.notice('SAUT IMPOSSIBLE DEPUIS LA SURFACE — REJOIGNEZ LE PORTEUR', true);
          else if (e.phase === 'selected') this.notice(`DESTINATION DU SAUT : ${target}`);
          break;
        }
        case 'convoy':
          if (e.phase === 'launched') this.notice('CONVOI EN ROUTE — ESCORTEZ-LE');
          else if (e.phase === 'arrived') this.notice('CONVOI ARRIVÉ — DESCENTE ENGAGÉE');
          else this.notice('CONVOI DÉTRUIT');
          break;
        case 'turretsDeployed':
          this.notice(`FORTERESSE EN ALERTE — ${e.count} TOURELLES À DÉTRUIRE`);
          break;
        case 'turretDestroyed':
          this.notice(e.remaining > 0 ? `TOURELLE DÉTRUITE — ${e.remaining} RESTANTE${e.remaining > 1 ? 'S' : ''}` : 'DERNIÈRE TOURELLE DÉTRUITE');
          break;
        case 'sectorEntered': {
          const sector = this.data.arena.sectors.find((s) => s.id === e.sector);
          if (sector) this.sectorAnnounce = { label: sector.label, time: this.now };
          break;
        }
        default:
          break;
      }
    }
  }

  /** Message central bref ; `throttle` évite de répéter le même message en rafale. */
  notice(text: string, throttle = false): void {
    if (throttle && text === this.noticeText && this.now - this.noticeTime < 1.2) return;
    this.noticeText = text;
    this.noticeTime = this.now;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  clear(): void {
    this.resize();
    this.ctx.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
  }

  draw(sim: Simulation, camera: TargetCamera, renderPositions: Map<number, Vector3>, dt: number): void {
    this.now += dt;
    this.resize();
    const ctx = this.ctx;
    const W = this.canvas.clientWidth, H = this.canvas.clientHeight;
    ctx.clearRect(0, 0, W, H);
    const state = sim.state;
    const player = state.player;
    const enhanced = this.settings.hudEnhanced;
    const lw = enhanced ? this.hud.lineWidthEnhanced : this.hud.lineWidth;
    const tlw = enhanced ? this.hud.threatLineWidthEnhanced : this.hud.threatLineWidth;
    const scale = enhanced ? 1.15 : 1;
    const c = this.hud.colors;

    const viewProj = camera.getViewMatrix().multiply(camera.getProjectionMatrix());
    const camPos = camera.position;
    const camForward = camera.getDirection(Vector3.Forward(true));
    const project = (p: Vector3) => {
      const behind = Vector3.Dot(p.subtract(camPos), camForward) <= 0.1;
      const s = Vector3.Project(p, Matrix.IdentityReadOnly, viewProj, { x: 0, y: 0, width: W, height: H } as never);
      const onScreen = !behind && s.x >= 0 && s.x <= W && s.y >= 0 && s.y <= H;
      return { x: s.x, y: s.y, onScreen, behind };
    };
    const playerPos = renderPositions.get(player.id) ?? new Vector3(...player.position.toArray());

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Ombre portée : textes et traits lisibles sur fond clair (intérieur du porteur, explosions).
    ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
    ctx.shadowBlur = 3;
    ctx.font = `600 ${Math.round(12 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    ctx.textBaseline = 'middle';

    const flying = player.alive && sim.control.mode === 'flight';
    if (player.alive) {
      this.drawSectors(sim, project, playerPos, scale);
      this.drawCarrierMarker(sim, project, playerPos, scale);
      this.drawConvoy(sim, project, renderPositions, playerPos, scale);
      if (flying) this.drawPickups(sim, project, playerPos, scale, tlw);
      const contacts = this.collectContacts(sim, project, renderPositions, playerPos);
      this.drawDrones(sim, contacts, scale);
      this.drawContacts(sim, contacts.filter((ct) => ct.ship.kind !== 'drone' || ct.ship.id === sim.state.weapons.lockTargetId), W, H, tlw, scale);
      if (flying) {
        this.drawStick(sim, project, scale);
        this.drawReticle(sim, project, lw, scale);
        this.drawDrift(sim, project, playerPos, lw);
        this.drawDockGuidance(sim, W, H, scale);
      }
      this.drawDamage(W, H, scale);
      this.drawRadar(sim, W, H, scale, lw);
    }
    this.drawControlOverlay(sim, W, H, scale);
    this.drawPanels(sim, W, H, scale, lw, c);
    this.drawScore(sim, W, scale);
    this.drawBanner(sim, W, H, scale);
    if (this.debugText) {
      ctx.fillStyle = 'rgba(200,230,255,0.85)';
      ctx.textAlign = 'left';
      ctx.font = `500 12px ui-monospace, Consolas, monospace`;
      this.debugText.split('\n').forEach((line, i) => ctx.fillText(line, 12, 120 + i * 15));
    }
  }

  private colorFor(ship: Ship, sim: Simulation): RGB {
    const c = this.hud.colors;
    if (ship.id === sim.state.weapons.lockTargetId && sim.state.weapons.lockState !== 'none') return c.acquisition;
    if (ship.kind === 'boss') return c.boss;
    if (ship.kind === 'heavy') return c.heavy;
    return c.hostile;
  }

  private collectContacts(sim: Simulation, project: (p: Vector3) => Contact['screen'], renderPositions: Map<number, Vector3>, playerPos: Vector3): Contact[] {
    const state = sim.state;
    const contacts: Contact[] = [];
    const lockId = state.weapons.lockState !== 'none' ? state.weapons.lockTargetId : null;
    for (const ship of state.ships) {
      if (!ship.alive || ship.team !== 'enemy') continue;
      const pos = renderPositions.get(ship.id) ?? new Vector3(...ship.position.toArray());
      const distance = Vector3.Distance(pos, playerPos);
      const charging = sim.brains.get(ship.id)?.isCharging ?? false;
      const isTarget = ship.id === lockId;
      // Boss et tourelles (objectifs) : marqués à toute distance.
      if (distance > this.hud.markerRange && ship.kind !== 'boss' && ship.kind !== 'turret' && !charging && !isTarget) continue;
      const base = isTarget ? 100 : ship.kind === 'boss' ? 80 : charging ? 70 : ship.kind === 'heavy' ? 40 : ship.kind === 'turret' ? 35 : ship.kind === 'marksman' ? 30 : 20;
      contacts.push({ ship, screen: project(pos), distance, priority: base - distance / 50 });
    }
    return contacts.sort((a, b) => b.priority - a.priority);
  }

  private drawContacts(sim: Simulation, contacts: Contact[], W: number, H: number, lw: number, scale: number): void {
    const ctx = this.ctx;
    const state = sim.state;
    let onScreen = 0, offScreen = 0;
    const lockId = state.weapons.lockTargetId;
    for (const contact of contacts) {
      const { ship, screen, distance } = contact;
      const color = this.colorFor(ship, sim);
      const charging = sim.brains.get(ship.id)?.isCharging ?? false;
      const isLock = ship.id === lockId && state.weapons.lockState !== 'none';
      if (screen.onScreen) {
        if (onScreen >= this.hud.onScreenBudget && !isLock) continue;
        onScreen++;
        const size = (ship.kind === 'boss' ? 16 : ship.kind === 'heavy' ? 12 : 10) * scale;
        const pulse = charging ? 0.55 + 0.45 * Math.abs(Math.sin(this.now * 12)) : 1;
        ctx.strokeStyle = css(color, 0.9 * pulse);
        ctx.lineWidth = charging ? lw * 1.8 : lw;
        drawGlyph(ctx, ship.kind, screen.x, screen.y, size);
        if (isLock) this.drawLock(sim, screen.x, screen.y, size, lw, scale, ship, distance);
        else if (charging) {
          ctx.fillStyle = css(color, pulse);
          ctx.textAlign = 'center';
          ctx.fillText('CHARGE', screen.x, screen.y - size - 10 * scale);
        }
      } else {
        if (offScreen >= this.hud.offScreenBudget) continue;
        offScreen++;
        // Flèche sur le bord, dans la direction du contact.
        let dx = screen.x - W / 2, dy = screen.y - H / 2;
        if (screen.behind) { dx = -dx; dy = -dy; }
        const angle = Math.atan2(dy, dx);
        const margin = 42 * scale;
        const rx = W / 2 - margin, ry = H / 2 - margin;
        const t = Math.min(rx / Math.max(Math.abs(Math.cos(angle)), 1e-3), ry / Math.max(Math.abs(Math.sin(angle)), 1e-3));
        const x = W / 2 + Math.cos(angle) * t, y = H / 2 + Math.sin(angle) * t;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.fillStyle = css(color, charging ? 0.6 + 0.4 * Math.abs(Math.sin(this.now * 12)) : 0.85);
        ctx.beginPath();
        ctx.moveTo(12 * scale, 0);
        ctx.lineTo(-6 * scale, -7 * scale);
        ctx.lineTo(-6 * scale, 7 * scale);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        ctx.fillStyle = css(color, 0.8);
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(distance)} m`, x - Math.cos(angle) * 22 * scale, y - Math.sin(angle) * 22 * scale);
      }
    }
  }

  private drawLock(sim: Simulation, x: number, y: number, size: number, lw: number, scale: number, ship: Ship, distance: number): void {
    const ctx = this.ctx;
    const w = sim.state.weapons;
    const amber = this.hud.colors.acquisition;
    const r = size + 9 * scale;
    ctx.strokeStyle = css(amber, 0.95);
    ctx.lineWidth = w.lockState === 'locked' ? lw * 1.6 : lw;
    if (w.lockState === 'locked') {
      const k = r * 0.45;
      for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        ctx.beginPath();
        ctx.moveTo(x + sx * r, y + sy * (r - k));
        ctx.lineTo(x + sx * r, y + sy * r);
        ctx.lineTo(x + sx * (r - k), y + sy * r);
        ctx.stroke();
      }
    } else {
      ctx.setLineDash(w.lockState === 'detected' ? [3, 4] : []);
      ctx.beginPath();
      ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0.08, lockProgress(sim.state)));
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // Fiche de la cible suivie : rôle, distance, coque.
    const label = ship.kind === 'boss' ? 'BOSS' : this.data.enemies.roles[ship.kind as 'interceptor'].label;
    ctx.fillStyle = css(amber, 0.95);
    ctx.textAlign = 'left';
    ctx.fillText(`${label}  ${Math.round(distance)} m${w.lockState === 'locked' ? '  VERROU' : ''}`, x + r + 6 * scale, y - 7 * scale);
    const barW = 46 * scale;
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(x + r + 6 * scale, y + 3 * scale, barW, 3 * scale);
    ctx.fillStyle = css(amber, 0.95);
    ctx.fillRect(x + r + 6 * scale, y + 3 * scale, barW * ship.healthRatio(), 3 * scale);
  }

  private drawReticle(sim: Simulation, project: (p: Vector3) => Contact['screen'], lw: number, scale: number): void {
    const ctx = this.ctx;
    const aim = laserAimPoint(sim.state);
    if (!aim) return;
    const s = project(new Vector3(aim.x, aim.y, aim.z));
    if (!s.onScreen) return;
    const cyan = this.hud.colors.player;
    const r = 9 * scale;
    ctx.strokeStyle = css(cyan, 0.9);
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
    ctx.stroke();
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      ctx.beginPath();
      ctx.moveTo(s.x + dx * (r + 3 * scale), s.y + dy * (r + 3 * scale));
      ctx.lineTo(s.x + dx * (r + 9 * scale), s.y + dy * (r + 9 * scale));
      ctx.stroke();
    }
    // Jauges autour du réticule : chaleur du laser à gauche, énergie de boost à droite.
    const w = sim.state.weapons;
    const player = sim.player;
    const gaugeR = r + 16 * scale;
    const arc = (from: number, to: number, value: number, color: string) => {
      ctx.lineWidth = 3 * scale;
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.beginPath();
      ctx.arc(s.x, s.y, gaugeR, from, to);
      ctx.stroke();
      if (value <= 0) return;
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.arc(s.x, s.y, gaugeR, to - (to - from) * Math.min(1, value), to);
      ctx.stroke();
    };
    if (this.data.ships.ships[w.shipId].laser) {
      const hot = w.overheated ? `rgba(255,60,40,${0.6 + 0.4 * Math.abs(Math.sin(this.now * 14))})` : w.heat > 0.7 ? 'rgba(255,140,40,0.95)' : 'rgba(80,220,255,0.85)';
      arc(Math.PI * 0.7, Math.PI * 1.3, w.heat, hot);
    }
    const boost = Number.isFinite(player.motor.boostCapacity) ? player.boostEnergy / player.motor.boostCapacity : 1;
    arc(-Math.PI * 0.3, Math.PI * 0.3, boost, player.boostExhausted ? 'rgba(255,120,60,0.9)' : 'rgba(90,150,255,0.9)');
    // Confirmations : touche (0,18 s) et destruction (0,45 s).
    const hit = 1 - (this.now - this.hitTime) / this.hud.hitConfirmDuration;
    const kill = 1 - (this.now - this.killTime) / this.hud.killConfirmDuration;
    const confirm = (alpha: number, size: number, color: string) => {
      if (alpha <= 0) return;
      ctx.strokeStyle = color.replace('A', String(alpha));
      ctx.lineWidth = lw * 1.4;
      for (const [dx, dy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
        ctx.beginPath();
        ctx.moveTo(s.x + dx * size * 0.45, s.y + dy * size * 0.45);
        ctx.lineTo(s.x + dx * size, s.y + dy * size);
        ctx.stroke();
      }
    };
    confirm(hit, 14 * scale, 'rgba(255,255,255,A)');
    confirm(kill, 24 * scale * (1.3 - kill * 0.3), 'rgba(255,200,80,A)');
  }

  /** Marqueur de dérive : où porte la vitesse actuelle. */
  private drawDrift(sim: Simulation, project: (p: Vector3) => Contact['screen'], playerPos: Vector3, lw: number): void {
    const player = sim.player;
    const speed = player.velocity.length();
    if (speed < 3) return;
    const ahead = player.velocity.normalized().scale(120);
    const s = project(playerPos.add(new Vector3(ahead.x, ahead.y, ahead.z)));
    if (!s.onScreen) return;
    const ctx = this.ctx;
    ctx.strokeStyle = css(this.hud.colors.player, 0.45);
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.arc(s.x, s.y, 4, 0, Math.PI * 2);
    ctx.moveTo(s.x - 9, s.y); ctx.lineTo(s.x - 5, s.y);
    ctx.moveTo(s.x + 5, s.y); ctx.lineTo(s.x + 9, s.y);
    ctx.moveTo(s.x, s.y - 5); ctx.lineTo(s.x, s.y - 9);
    ctx.stroke();
  }

  /** Secteurs : losange discret ; nom seulement de près ou pour le secteur d'arrivée annoncé. */
  private drawSectors(sim: Simulation, project: (p: Vector3) => Contact['screen'], playerPos: Vector3, scale: number): void {
    const ctx = this.ctx;
    const announced = sim.mission.phase === 'intermission' ? sim.mission.nextArrival() : null;
    for (const sector of this.data.arena.sectors) {
      const center = new Vector3(...sector.center);
      const s = project(center);
      if (!s.onScreen) continue;
      const d = Vector3.Distance(center, playerPos);
      if (d < sector.visitRadius * 0.5) continue;
      const highlight = sector.id === announced;
      ctx.strokeStyle = css(sector.color, highlight ? 0.9 : 0.35);
      ctx.lineWidth = 1;
      drawGlyph(ctx, 'boss', s.x, s.y, (highlight ? 8 : 5) * scale);
      if (highlight || d < 320) {
        ctx.fillStyle = css(sector.color, highlight ? 0.9 : 0.55);
        ctx.textAlign = 'center';
        ctx.fillText(`${sector.label} ${Math.round(d)} m`, s.x, s.y + 14 * scale);
      }
    }
  }

  /** Manche virtuel : zone de pilotage et curseur (la position fixe la vitesse de virage). */
  /** Manche virtuel centré sur le réticule du canon : un seul repère pour piloter et viser. */
  private drawStick(sim: Simulation, project: (p: Vector3) => Contact['screen'], scale: number): void {
    if (sim.pilot.mouseMode !== 'joystick') return;
    const aim = laserAimPoint(sim.state);
    if (!aim) return;
    const center = project(new Vector3(aim.x, aim.y, aim.z));
    if (!center.onScreen) return;
    const ctx = this.ctx;
    const R = 46 * scale;
    const cx = center.x, cy = center.y;
    const stick = sim.pilot.stick;
    const x = cx + stick.x * R, y = cy - stick.y * R;
    const dead = this.data.ships.flight.mouseJoystick.deadZone;
    ctx.strokeStyle = 'rgba(120,220,255,0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, R * dead, 0, Math.PI * 2);
    ctx.stroke();
    const deflection = Math.hypot(stick.x, stick.y);
    if (deflection > dead) {
      ctx.strokeStyle = `rgba(120,220,255,${0.25 + 0.35 * deflection})`;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(160,235,255,0.9)';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(x, y, 5 * scale, 0, Math.PI * 2);
    ctx.stroke();
  }

  /** Drones : petites marques hors budget de marqueurs (nombreux, menace collective). */
  private drawDrones(sim: Simulation, contacts: Contact[], scale: number): void {
    const ctx = this.ctx;
    const color = this.hud.colors.heavy;
    for (const c of contacts) {
      if (c.ship.kind !== 'drone' || !c.screen.onScreen || c.ship.id === sim.state.weapons.lockTargetId) continue;
      const dive = (sim.brains.get(c.ship.id) as { droneDiveState?: string } | undefined)?.droneDiveState ?? 'orbit';
      if (dive !== 'orbit') {
        // Piqué annoncé ou en cours : marque rouge clignotante, bien visible.
        const pulse = dive === 'warn' ? 0.5 + 0.5 * Math.abs(Math.sin(this.now * 16)) : 1;
        ctx.strokeStyle = `rgba(255,50,40,${pulse})`;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.arc(c.screen.x, c.screen.y, 11 * scale, 0, Math.PI * 2);
        ctx.stroke();
        drawGlyph(ctx, 'drone', c.screen.x, c.screen.y, 6 * scale);
        ctx.fillStyle = `rgba(255,80,60,${pulse})`;
        ctx.textAlign = 'center';
        ctx.fillText(dive === 'warn' ? 'PIQUÉ' : '!', c.screen.x, c.screen.y - 17 * scale);
        continue;
      }
      const near = c.distance < 60;
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = css(color, near ? 0.95 : 0.6);
      drawGlyph(ctx, 'drone', c.screen.x, c.screen.y, (near ? 6 : 4) * scale);
    }
  }

  /** Convoi escorté : repère vert permanent, coque et distance. */
  private drawConvoy(sim: Simulation, project: (p: Vector3) => Contact['screen'], renderPositions: Map<number, Vector3>, playerPos: Vector3, scale: number): void {
    const convoy = sim.mission.convoy;
    if (!convoy) return;
    const ctx = this.ctx;
    const pos = renderPositions.get(convoy.id) ?? new Vector3(...convoy.position.toArray());
    const s = project(pos.add(new Vector3(0, convoy.radius * 0.6, 0)));
    const ratio = convoy.healthRatio();
    const color: RGB = ratio > 0.35 ? [0.3, 1, 0.6] : [1, 0.4, 0.2];
    const pulse = convoy.lastDamageTime > sim.time - 0.4 ? 0.55 + 0.45 * Math.abs(Math.sin(this.now * 14)) : 0.9;
    ctx.strokeStyle = css(color, pulse);
    ctx.lineWidth = 1.6;
    ctx.fillStyle = css(color, pulse);
    ctx.textAlign = 'center';
    if (s.onScreen) {
      const r = 11 * scale;
      ctx.beginPath();
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x + r, s.y);
      ctx.lineTo(s.x, s.y + r);
      ctx.lineTo(s.x - r, s.y);
      ctx.closePath();
      ctx.stroke();
      ctx.fillText(`${sim.state.zoneData?.convoy?.label.toUpperCase() ?? 'CONVOI'} ${Math.round(ratio * 100)} % · ${Math.round(Vector3.Distance(pos, playerPos))} m`, s.x, s.y + 20 * scale);
    }
  }

  /** Docks (station, porteur) : repères permanents, mis en avant quand la coque ou les munitions sont basses. */
  private drawCarrierMarker(sim: Simulation, project: (p: Vector3) => Contact['screen'], playerPos: Vector3, scale: number): void {
    if (sim.control.mode !== 'flight') return;
    const ctx = this.ctx;
    const w = sim.state.weapons;
    const needs = sim.player.healthRatio() < 0.45 || (w.maxMissiles > 0 && w.missiles === 0) || (w.maxSwarm > 0 && w.swarm < w.maxSwarm * 0.2);
    const pulse = needs ? 0.55 + 0.45 * Math.abs(Math.sin(this.now * 5)) : 0.7;
    for (const dock of sim.docks) {
      const top = Math.max(...dock.data.parts.map((p) => p.center[1]! + p.half[1]!));
      const target = new Vector3(dock.position.x, dock.position.y + top + 6, dock.position.z);
      const s = project(target);
      if (!s.onScreen) continue;
      const d = Vector3.Distance(target, playerPos);
      const color: RGB = dock.id === 'station' ? [1, 0.75, 0.3] : [0.3, 1, 0.6];
      ctx.strokeStyle = css(color, pulse);
      ctx.lineWidth = needs ? 2 : 1.2;
      ctx.strokeRect(s.x - 7 * scale, s.y - 5 * scale, 14 * scale, 10 * scale);
      ctx.fillStyle = css(color, pulse);
      ctx.textAlign = 'center';
      ctx.fillText(`${dock.id === 'station' ? 'STATION' : 'PORTEUR'} ${Math.round(d)} m${needs ? ' · RÉPARATION' : ''}`, s.x, s.y + 15 * scale);
    }
  }

  /** Guidage d'appontage dans la zone d'approche : conditions vitesse / alignement. */
  private drawDockGuidance(sim: Simulation, W: number, H: number, scale: number): void {
    const dock = sim.docks.find((d) => d.readiness(sim.player).inZone);
    if (!dock) return;
    const ready = dock.readiness(sim.player);
    const ctx = this.ctx;
    const max = dock.data.dockMaxSpeed;
    const speed = Math.round(sim.player.velocity.length());
    ctx.textAlign = 'center';
    ctx.font = `700 ${Math.round(14 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    ctx.fillStyle = ready.speedOk && ready.alignOk ? 'rgba(90,255,150,0.95)' : 'rgba(255,190,60,0.95)';
    ctx.fillText(dock.id === 'station' ? 'APPROCHE DE LA STATION' : 'APPROCHE DU PORTEUR', W / 2, H * 0.62);
    ctx.font = `600 ${Math.round(12 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    const speedText = `VITESSE ${speed} / ${max} m/s ${ready.speedOk ? 'OK' : '— FREINEZ'}`;
    const alignText = `ALIGNEMENT ${ready.alignOk ? 'OK' : '— VISEZ LE TUNNEL'}`;
    ctx.fillText(`${speedText}   ·   ${alignText}`, W / 2, H * 0.62 + 18 * scale);
  }

  /** Séquences du porteur et destruction : messages centraux, panneau de service. */
  private drawControlOverlay(sim: Simulation, W: number, H: number, scale: number): void {
    const ctx = this.ctx;
    const control = sim.control;
    const player = sim.player;
    const w = sim.state.weapons;
    ctx.textAlign = 'center';
    const big = `700 ${Math.round(18 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    const small = `600 ${Math.round(13 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    if (control.mode === 'docking') {
      ctx.font = big;
      ctx.fillStyle = 'rgba(90,255,150,0.95)';
      ctx.fillText('APPONTAGE EN COURS', W / 2, H * 0.3);
    } else if (control.mode === 'docked') {
      const x = W / 2 - 160 * scale, y = H * 0.26;
      ctx.fillStyle = 'rgba(2,12,24,0.7)';
      ctx.fillRect(x - 16, y - 28 * scale, 352 * scale, 112 * scale);
      ctx.font = big;
      ctx.fillStyle = 'rgba(90,255,150,0.95)';
      ctx.fillText('À QUAI — SERVICE', W / 2, y - 6 * scale);
      const bar = (row: number, label: string, ratio: number) => {
        const by = y + 12 * scale + row * 18 * scale;
        ctx.textAlign = 'left';
        ctx.font = small;
        ctx.fillStyle = 'rgba(200,230,255,0.85)';
        ctx.fillText(label, x, by + 4 * scale);
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.fillRect(x + 110 * scale, by, 200 * scale, 6 * scale);
        ctx.fillStyle = ratio >= 1 ? 'rgba(90,255,150,0.95)' : 'rgba(80,200,255,0.95)';
        ctx.fillRect(x + 110 * scale, by, 200 * scale * Math.min(1, ratio), 6 * scale);
      };
      bar(0, 'COQUE', player.health / player.maxHealth);
      bar(1, 'MISSILES', w.maxMissiles > 0 ? w.missiles / w.maxMissiles : 1);
      if (w.maxSwarm > 0) bar(2, 'NUÉE', w.swarm / w.maxSwarm);
      ctx.textAlign = 'center';
      ctx.font = small;
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      const auto = Number.isFinite(control.autoLaunchAt) ? ` · catapulte dans ${Math.max(0, control.autoLaunchAt - control.elapsed).toFixed(1)} s` : '';
      ctx.fillText(`AVANCER ou BOOST : décoller${auto}`, W / 2, y + 74 * scale);
    } else if (control.mode === 'launching') {
      ctx.font = big;
      ctx.fillStyle = 'rgba(80,200,255,0.95)';
      ctx.fillText('CATAPULTE', W / 2, H * 0.3);
    } else if (control.mode === 'destroyed') {
      const delay = this.data.carrier.respawn.delay;
      ctx.font = big;
      ctx.fillStyle = 'rgba(255,90,70,0.95)';
      ctx.fillText('APPAREIL DÉTRUIT', W / 2, H * 0.4);
      ctx.font = small;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      const remaining = Math.max(0, delay - control.elapsed).toFixed(1);
      const reserve = Number.isFinite(control.reserves) ? `${control.reserves} en réserve` : 'réserve illimitée';
      const where = sim.dock.id === 'station' ? 'à la station' : 'au porteur';
      ctx.fillText(control.reserves > 0 ? `Réapparition ${where} dans ${remaining} s · ${reserve}` : `Plus d’appareil en réserve : mission perdue · réapparition ${where} dans ${remaining} s`, W / 2, H * 0.4 + 22 * scale);
    }
  }

  /** Score et multiplicateur de série (haut droite). */
  private drawScore(sim: Simulation, W: number, scale: number): void {
    const ctx = this.ctx;
    const score = sim.state.score;
    const x = W - 26 * scale;
    const pulse = Math.max(0, 1 - (this.now - this.scorePulse) / 0.35);
    ctx.textAlign = 'right';
    ctx.font = `700 ${Math.round((22 + pulse * 6) * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    ctx.fillStyle = `rgba(255,255,255,${0.85 + 0.15 * pulse})`;
    ctx.fillText(score.total.toLocaleString('fr-FR'), x, 30 * scale);
    if (score.multiplier > 1) {
      const window = this.data.waves.scoring.streakWindow;
      const ratio = Math.max(0, Math.min(1, score.streakTimer / window));
      ctx.font = `700 ${Math.round(15 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      ctx.fillStyle = 'rgba(255,200,80,0.95)';
      ctx.fillText(`×${score.multiplier.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}`, x, 52 * scale);
      ctx.fillStyle = 'rgba(255,200,80,0.3)';
      ctx.fillRect(x - 70 * scale, 60 * scale, 70 * scale, 3 * scale);
      ctx.fillStyle = 'rgba(255,200,80,0.95)';
      ctx.fillRect(x - 70 * scale * ratio, 60 * scale, 70 * scale * ratio, 3 * scale);
    }
    if (pulse > 0 && this.lastGain > 0) {
      ctx.font = `600 ${Math.round(13 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      ctx.fillStyle = `rgba(255,220,120,${pulse})`;
      ctx.fillText(`+${this.lastGain}`, x, 76 * scale);
    }
  }

  private drawPickups(sim: Simulation, project: (p: Vector3) => Contact['screen'], playerPos: Vector3, scale: number, lw: number): void {
    const ctx = this.ctx;
    for (const pickup of sim.state.pickups) {
      const pos = new Vector3(...pickup.position.toArray());
      const d = Vector3.Distance(pos, playerPos);
      const range = pickup.kind === 'arena' ? this.hud.arenaPickupMarkerRange : this.hud.pickupMarkerRange;
      if (d > range) continue;
      const s = project(pos);
      if (!s.onScreen) continue;
      ctx.strokeStyle = css(this.hud.colors.resource, 0.85);
      ctx.lineWidth = lw;
      hexagon(ctx, s.x, s.y, (pickup.kind === 'arena' ? 8 : 6) * scale);
      if (pickup.kind === 'arena') {
        ctx.fillStyle = css(this.hud.colors.resource, 0.8);
        ctx.textAlign = 'center';
        ctx.fillText(`MISSILES ${Math.round(d)} m`, s.x, s.y + 15 * scale);
      }
    }
  }

  /** Indicateur de dégâts reçus : direction, source, intensité (0,75 s). */
  private drawDamage(W: number, H: number, scale: number): void {
    const ctx = this.ctx;
    const duration = this.hud.damageFeedbackDuration;
    this.damages = this.damages.filter((d) => this.now - d.time < duration);
    const camRight = this.cameraBasis.right, camUp = this.cameraBasis.up, camFwd = this.cameraBasis.forward;
    for (const d of this.damages) {
      const alpha = 1 - (this.now - d.time) / duration;
      const x = d.dir.dot(camRight), y = d.dir.dot(camUp), z = d.dir.dot(camFwd);
      const angle = Math.atan2(-y, x);
      const radius = Math.min(W, H) * 0.17;
      const weight = Math.min(1, d.amount / 25);
      ctx.strokeStyle = d.shield
        ? `rgba(110,190,255,${alpha * (0.45 + 0.4 * weight)})`
        : `rgba(255,${Math.round(70 - 50 * weight)},40,${alpha * (0.5 + 0.5 * weight)})`;
      ctx.lineWidth = (3 + 5 * weight) * scale;
      ctx.beginPath();
      ctx.arc(W / 2, H / 2, radius, angle - 0.32, angle + 0.32);
      ctx.stroke();
      if (z < -0.3) {
        // Coup venu de l'arrière : marque basse supplémentaire.
        ctx.beginPath();
        ctx.arc(W / 2, H / 2, radius + 8 * scale, Math.PI / 2 - 0.25, Math.PI / 2 + 0.25);
        ctx.stroke();
      }
      ctx.fillStyle = d.shield ? `rgba(140,200,255,${alpha})` : `rgba(255,120,90,${alpha})`;
      ctx.textAlign = 'center';
      ctx.fillText(`${SOURCE_LABEL[d.source]} −${Math.round(d.amount)}`, W / 2 + Math.cos(angle) * (radius + 26 * scale), H / 2 + Math.sin(angle) * (radius + 26 * scale));
    }
  }

  /** Base caméra mise à jour par l'appelant (pour orienter l'indicateur de dégâts). */
  cameraBasis = { right: new Vec3(1, 0, 0), up: new Vec3(0, 1, 0), forward: new Vec3(0, 0, -1) };

  /** Radar 200 m, vue du dessus dans le repère du joueur ; altitude par tige verticale. */
  private drawRadar(sim: Simulation, W: number, H: number, scale: number, lw: number): void {
    const ctx = this.ctx;
    const R = 78 * scale;
    const cx = W - R - 26 * scale, cy = H - R - 26 * scale;
    this.drawRadarBody(sim, W, H, scale, lw);
    // Brouillage (nébuleuse) : parasites et mention, portée réduite.
    const jam = sim.state.zoneData?.radarScale ?? 1;
    if (jam < 1) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.clip();
      for (let i = 0; i < 26; i++) {
        const y = cy - R + Math.random() * 2 * R;
        ctx.strokeStyle = `rgba(190,150,255,${(0.08 + Math.random() * 0.22).toFixed(2)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - R, y);
        ctx.lineTo(cx + R, y + (Math.random() - 0.5) * 6);
        ctx.stroke();
      }
      ctx.restore();
      ctx.fillStyle = css([0.8, 0.6, 1], 0.6 + 0.4 * Math.abs(Math.sin(this.now * 3)));
      ctx.textAlign = 'center';
      ctx.font = `700 ${Math.round(11 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      ctx.fillText('BROUILLAGE', cx, cy - R - 10 * scale);
    }
  }

  private drawRadarBody(sim: Simulation, W: number, H: number, scale: number, lw: number): void {
    const ctx = this.ctx;
    const R = 78 * scale;
    const cx = W - R - 26 * scale, cy = H - R - 26 * scale;
    const range = this.hud.radarRange;
    const player = sim.player;
    ctx.fillStyle = 'rgba(2,10,20,0.45)';
    ctx.strokeStyle = css(this.hud.colors.player, 0.4);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, R / 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = css(this.hud.colors.player, 0.9);
    ctx.beginPath();
    ctx.moveTo(cx, cy - 5 * scale);
    ctx.lineTo(cx - 4 * scale, cy + 4 * scale);
    ctx.lineTo(cx + 4 * scale, cy + 4 * scale);
    ctx.fill();
    const inv = player.rotation.conjugate();
    const plot = (world: Vec3, color: RGB, kind: string, size: number) => {
      const local = inv.rotate(world.sub(player.position));
      const horizontal = Math.hypot(local.x, local.z);
      let k = R / range;
      if (horizontal * k > R - 4) k = (R - 4) / Math.max(horizontal, 1e-3);
      const x = cx + local.x * k, y = cy + local.z * k;
      const stem = Math.max(-18, Math.min(18, (local.y / range) * R)) * 0.8;
      ctx.strokeStyle = css(color, 0.55);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y - stem);
      ctx.stroke();
      ctx.strokeStyle = css(color, 0.95);
      ctx.lineWidth = lw;
      drawGlyph(ctx, kind, x, y - stem, size);
    };
    for (const pickup of sim.state.pickups) plot(pickup.position, this.hud.colors.resource, 'resource', 3 * scale);
    for (const ship of sim.state.ships) {
      if (!ship.alive || ship.team !== 'enemy') continue;
      plot(ship.position, this.colorFor(ship, sim), ship.kind, (ship.kind === 'boss' ? 6 : 4) * scale);
    }
    ctx.fillStyle = css(this.hud.colors.player, 0.6);
    ctx.textAlign = 'center';
    ctx.fillText(`${range} m`, cx, cy + R + 11 * scale);
  }

  private drawPanels(sim: Simulation, W: number, H: number, scale: number, lw: number, c: HudData['colors']): void {
    const ctx = this.ctx;
    const state = sim.state;
    const player = state.player;
    const w = state.weapons;
    const profile = this.data.ships.ships[w.shipId];
    const x0 = 26 * scale, y0 = H - 26 * scale;
    // Coque.
    const ratio = player.healthRatio();
    const hullColor: RGB = ratio > 0.5 ? c.player : ratio > 0.25 ? c.acquisition : c.hostile;
    ctx.textAlign = 'left';
    ctx.fillStyle = css(c.player, 0.7);
    const reserves = sim.control.reserves;
    const reserveText = Number.isFinite(reserves) ? ` · RÉSERVE ${reserves > 0 ? '▲'.repeat(reserves) : 'AUCUNE'}` : '';
    ctx.fillText(`${profile.label} · COQUE${reserveText}`, x0, y0 - 62 * scale);
    // Bouclier : fine jauge bleue au-dessus de la coque ; scintille quand il encaisse, rouge pâle une fois rompu.
    if (player.maxShield > 0) {
      const sr = player.shield / player.maxShield;
      const hit = Math.max(0, 1 - (this.now - this.shieldHitTime) / 0.35);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(x0, y0 - 54 * scale, 220 * scale, 5 * scale);
      ctx.fillStyle = `rgba(${Math.round(90 + 165 * hit)},${Math.round(170 + 85 * hit)},255,0.95)`;
      ctx.fillRect(x0, y0 - 54 * scale, 220 * scale * sr, 5 * scale);
      ctx.strokeStyle = sr > 0 ? 'rgba(90,170,255,0.55)' : 'rgba(255,110,90,0.5)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x0, y0 - 54 * scale, 220 * scale, 5 * scale);
      ctx.fillStyle = sr > 0 ? 'rgba(120,190,255,0.95)' : 'rgba(255,130,110,0.9)';
      ctx.fillText(sr > 0 ? `BOUCLIER ${Math.ceil(player.shield)}` : 'BOUCLIER ROMPU', x0 + 228 * scale, y0 - 50 * scale);
    }
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(x0, y0 - 42 * scale, 220 * scale, 8 * scale);
    ctx.fillStyle = css(hullColor, 0.95);
    ctx.fillRect(x0, y0 - 42 * scale, 220 * scale * ratio, 8 * scale);
    ctx.strokeStyle = css(c.player, 0.5);
    ctx.lineWidth = 1;
    ctx.strokeRect(x0, y0 - 42 * scale, 220 * scale, 8 * scale);
    ctx.fillStyle = css(hullColor, 0.95);
    ctx.fillText(`${Math.ceil(player.health)} / ${player.maxHealth}`, x0 + 228 * scale, y0 - 38 * scale);

    // Vitesse et enveloppe.
    const f = player.flight;
    const envelope = f.braking ? 'FREIN' : f.boosting ? 'BOOST' : f.reversing ? 'RECUL' : '';
    ctx.font = `700 ${Math.round(22 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    ctx.fillStyle = css(c.player, 0.95);
    ctx.fillText(`${Math.round(player.velocity.length())}`, x0, y0 - 12 * scale);
    ctx.font = `600 ${Math.round(12 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    ctx.fillText('m/s', x0 + 46 * scale, y0 - 10 * scale);
    if (envelope) {
      ctx.fillStyle = envelope === 'BOOST' ? 'rgba(80,150,255,0.95)' : 'rgba(255,120,40,0.95)';
      ctx.fillText(envelope, x0 + 76 * scale, y0 - 10 * scale);
    }
    // Accélérateur : jauge à droite de la vitesse (cyan en avant, orange en arrière, repère du neutre).
    if (this.settings.throttleMode === 'throttle') {
      const throttle = sim.pilot.throttle;
      const gx = x0 + 130 * scale, gy = y0 - 14 * scale, gw = 120 * scale, gh = 6 * scale;
      const zero = gx + gw * 0.5;
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(gx, gy, gw, gh);
      ctx.fillStyle = throttle >= 0 ? 'rgba(0,220,255,0.9)' : 'rgba(255,140,40,0.9)';
      if (throttle >= 0) ctx.fillRect(zero, gy, (gw / 2) * throttle, gh);
      else ctx.fillRect(zero + (gw / 2) * throttle, gy, (gw / 2) * -throttle, gh);
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillRect(zero - 1, gy - 2, 2, gh + 4);
      ctx.fillStyle = css(c.player, 0.85);
      ctx.textAlign = 'left';
      ctx.fillText(`POUSSÉE ${Math.round(throttle * 100)} %`, gx + gw + 8 * scale, gy + 3 * scale);
    }

    // Munitions.
    let ammoText = profile.primary === 'laser' ? `LASER ∞   MISSILES ${w.missiles}/${w.maxMissiles}` : `MISSILES ${w.missiles}/${w.maxMissiles}`;
    if (profile.secondary === 'swarm') ammoText += `   NUÉE ${w.swarm}/${w.maxSwarm}`;
    if (state.equipment.emergencyRepair) ammoText += `   RÉPARATION ${sim.control.repairCharges > 0 ? `×${sim.control.repairCharges}` : 'UTILISÉE'}`;
    ctx.fillStyle = css(c.player, 0.85);
    ctx.fillText(ammoText, x0, y0 + 8 * scale);
    void lw;

    // Notifications : réparation, ressource, verrou.
    const repair = 1 - (this.now - this.repairTime) / this.hud.repairFeedbackDuration;
    if (repair > 0) {
      ctx.fillStyle = `rgba(90,255,170,${repair})`;
      ctx.fillText(`${this.repairLabel} +${Math.round(this.repairAmount)} COQUE`, x0, y0 - 80 * scale);
    }
    const notice = 1 - (this.now - this.noticeTime) / 1.2;
    if (notice > 0) {
      ctx.fillStyle = css(c.acquisition, notice);
      ctx.textAlign = 'center';
      ctx.fillText(this.noticeText, W / 2, H / 2 + 62 * scale);
    }

    // Limite de l'arène.
    const limit = this.data.arena.halfExtent;
    const p = player.position;
    const toEdge = limit - Math.max(Math.abs(p.x), Math.abs(p.y), Math.abs(p.z));
    if (player.alive && toEdge < this.data.arena.boundaryWarning) {
      ctx.fillStyle = `rgba(255,160,60,${0.6 + 0.4 * Math.abs(Math.sin(this.now * 5))})`;
      ctx.textAlign = 'center';
      ctx.fillText(`LIMITE DE L'ARÈNE — ${Math.max(0, Math.round(toEdge))} m`, W / 2, 96 * scale);
    }
    if (this.sectorAnnounce && this.now - this.sectorAnnounce.time < 2.2) {
      ctx.fillStyle = css(c.player, 1 - (this.now - this.sectorAnnounce.time) / 2.2);
      ctx.textAlign = 'center';
      ctx.fillText(this.sectorAnnounce.label, W / 2, H * 0.3);
    }
  }

  /** Module de saut (vol libre) : rappel de la touche, ou jauge de charge vers la zone visée. */
  private drawJumpDrive(sim: Simulation, W: number, scale: number): void {
    const drive = sim.state.equipment.jumpDrive;
    if (!drive) return;
    const ctx = this.ctx;
    const c = this.hud.colors;
    const control = sim.control;
    const target = control.jumpCharge >= 0 ? control.jumpTarget ?? '' : sim.jumpDestination() ?? '';
    const label = this.data.zones.zones[target]?.label.toUpperCase() ?? target;
    ctx.textAlign = 'center';
    ctx.font = `600 ${Math.round(12 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    if (control.jumpCharge < 0) {
      ctx.fillStyle = css(c.player, 0.55);
      ctx.fillText(`J : SAUT VERS ${label}${sim.jumpTargets().length > 1 ? ' · G : CHANGER' : ''}`, W / 2, 48 * scale);
      return;
    }
    const k = Math.min(1, control.jumpCharge / drive.charge);
    const barW = 260 * scale, x = W / 2 - barW / 2, y = 56 * scale;
    ctx.fillStyle = css([0.55, 0.78, 1], 0.95);
    ctx.fillText(`CHARGE DU SAUT VERS ${label} · ${Math.max(0, drive.charge - control.jumpCharge).toFixed(1).replace('.', ',')} s — J : ANNULER`, W / 2, 48 * scale);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(x, y, barW, 5 * scale);
    ctx.fillStyle = css([0.55, 0.78, 1], 0.95);
    ctx.fillRect(x, y, barW * k, 5 * scale);
  }

  /** Bandeau haut : mission (vague, ennemis restants, intermission, boss) ou vol libre. */
  private drawBanner(sim: Simulation, W: number, _H: number, scale: number): void {
    const ctx = this.ctx;
    const mission = sim.mission;
    const c = this.hud.colors;
    ctx.textAlign = 'center';
    ctx.font = `700 ${Math.round(15 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
    const sectorLabel = (id: SectorId) => this.data.arena.sectors.find((s) => s.id === id)?.label ?? id;
    if (!mission.active) {
      const outcome = mission.phase === 'victory' ? 'MISSION ACCOMPLIE · ' : mission.phase === 'defeat' ? 'MISSION ÉCHOUÉE · ' : '';
      ctx.font = `600 ${Math.round(13 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      ctx.fillStyle = css(c.player, 0.7);
      const where = sim.docks.some((d) => d.id === 'station') ? 'missions à la station ou au porteur' : `${this.data.zones.zones[sim.zone]?.label.toUpperCase() ?? ''} · missions et retour au porteur`;
      ctx.fillText(`${outcome}VOL LIBRE — ${where}`, W / 2, 30 * scale);
      this.drawJumpDrive(sim, W, scale);
    } else if (mission.phase === 'starting') {
      ctx.fillStyle = css(c.player, 0.9);
      ctx.fillText(`${mission.label.toUpperCase()} · CONTACT DANS ${Math.max(0, mission.timer).toFixed(0)} s`, W / 2, 30 * scale);
    } else if (mission.phase === 'objective' && mission.scenario?.objective === 'escort') {
      ctx.fillStyle = css([0.3, 1, 0.6], 0.95);
      ctx.fillText(`PROTÉGEZ LE CONVOI · ROUTE ${Math.round(mission.convoyProgress() * 100)} %`, W / 2, 30 * scale);
    } else if (mission.phase === 'objective') {
      ctx.fillStyle = css(c.boss, 0.95);
      ctx.fillText(`DÉTRUISEZ LES TOURELLES · ${mission.turrets.size}/${mission.turretTotal} RESTANTE${mission.turrets.size > 1 ? 'S' : ''}`, W / 2, 30 * scale);
    } else if (mission.phase === 'wave') {
      ctx.fillStyle = css(c.player, 0.9);
      const convoy = mission.convoy;
      const turrets = mission.turretTotal > 0 ? ` · TOURELLES ${mission.turrets.size}/${mission.turretTotal}`
        : convoy ? ` · CONVOI ${Math.round(convoy.healthRatio() * 100)} % · ROUTE ${Math.round(mission.convoyProgress() * 100)} %` : '';
      ctx.fillText(`VAGUE ${mission.waveIndex + 1}/${mission.totalWaves} · ${mission.enemiesRemaining} HOSTILE${mission.enemiesRemaining > 1 ? 'S' : ''}${turrets}`, W / 2, 30 * scale);
    } else if (mission.phase === 'intermission') {
      ctx.fillStyle = css(c.player, 0.95);
      const next = mission.waveIndex + 1 >= mission.totalWaves ? 'BOSS' : `VAGUE ${mission.waveIndex + 2}`;
      ctx.fillText(`${next} DANS ${mission.timer.toFixed(1)} s · DEPUIS ${sectorLabel(mission.nextArrival())}`, W / 2, 30 * scale);
      ctx.font = `600 ${Math.round(12 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      ctx.fillStyle = css(c.player, 0.6);
      ctx.fillText(`N : passer l’attente · module de missiles au ${sectorLabel('amberWell')}`, W / 2, 48 * scale);
    } else if (mission.phase === 'boss') {
      const brain = sim.bossBrain();
      const boss = sim.state.findShip(mission.bossId);
      if (brain && boss) {
        const status = brain.status();
        const barW = Math.min(520, W * 0.45) * scale;
        const x = W / 2 - barW / 2, y = 22 * scale;
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(x, y, barW, 8 * scale);
        const phaseColor: RGB = status.phase === 'opening' ? [1, 0.15, 0.05] : status.phase === 'assault' ? [1, 0.5, 0.05] : [0.75, 0.15, 1];
        ctx.fillStyle = css(phaseColor, 0.95);
        ctx.fillRect(x, y, barW * boss.healthRatio(), 8 * scale);
        ctx.strokeStyle = css(c.boss, 0.6);
        ctx.lineWidth = 1;
        for (const t of [this.data.boss.assaultThreshold, this.data.boss.overdriveThreshold]) {
          ctx.beginPath();
          ctx.moveTo(x + barW * t, y - 2);
          ctx.lineTo(x + barW * t, y + 10 * scale);
          ctx.stroke();
        }
        ctx.fillStyle = css(phaseColor, 1);
        ctx.fillText(`BOSS · ${PHASE_LABEL[status.phase]}`, W / 2, y + 22 * scale);
        const urgent = status.specialTelegraph || status.volleyCharging;
        ctx.fillStyle = urgent ? `rgba(255,120,255,${0.6 + 0.4 * Math.abs(Math.sin(this.now * 10))})` : status.recovery > 0 ? 'rgba(120,255,170,0.95)' : 'rgba(255,255,255,0.7)';
        ctx.font = `700 ${Math.round(14 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
        ctx.fillText(status.cue, W / 2, y + 40 * scale);
      }
    }
    if (this.tutorialText) {
      ctx.font = `600 ${Math.round(15 * scale)}px "Bahnschrift", "Segoe UI", sans-serif`;
      const lines = this.tutorialText.split('\n');
      const y = _H * 0.72;
      ctx.fillStyle = 'rgba(2,10,20,0.6)';
      const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
      ctx.fillRect(W / 2 - widest / 2 - 16, y - 18 * scale, widest + 32, lines.length * 20 * scale + 16 * scale);
      ctx.fillStyle = css(c.player, 0.95);
      lines.forEach((line, i) => ctx.fillText(line, W / 2, y + i * 20 * scale));
    }
  }
}

/** Glyphes : intercepteur triangle ; tireur triangle à trait central ; lourd crochets ; boss/secteur losange ; ressource hexagone. */
function drawGlyph(ctx: CanvasRenderingContext2D, kind: string, x: number, y: number, s: number): void {
  ctx.beginPath();
  switch (kind) {
    case 'interceptor':
    case 'marksman':
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s * 0.9, y + s * 0.7);
      ctx.lineTo(x - s * 0.9, y + s * 0.7);
      ctx.closePath();
      if (kind === 'marksman') {
        ctx.moveTo(x, y - s * 0.35);
        ctx.lineTo(x, y + s * 0.45);
      }
      break;
    case 'drone':
      ctx.moveTo(x - s, y - s * 0.5);
      ctx.lineTo(x, y + s * 0.6);
      ctx.lineTo(x + s, y - s * 0.5);
      break;
    case 'heavy':
      ctx.moveTo(x - s * 0.5, y - s);
      ctx.lineTo(x - s, y - s);
      ctx.lineTo(x - s, y + s);
      ctx.lineTo(x - s * 0.5, y + s);
      ctx.moveTo(x + s * 0.5, y - s);
      ctx.lineTo(x + s, y - s);
      ctx.lineTo(x + s, y + s);
      ctx.lineTo(x + s * 0.5, y + s);
      break;
    case 'resource':
      hexagon(ctx, x, y, s);
      return;
    case 'turret':
      ctx.rect(x - s * 0.8, y - s * 0.8, s * 1.6, s * 1.6);
      ctx.moveTo(x - s * 0.3, y);
      ctx.lineTo(x + s * 0.3, y);
      break;
    default:
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s, y);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s, y);
      ctx.closePath();
  }
  ctx.stroke();
}

function hexagon(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    const px = x + Math.cos(a) * s, py = y + Math.sin(a) * s;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
}
